import React from "react";
// import { PlusPromoIcon } from "../../packages/excalidraw/components/icons";
import { MainMenu } from "../../packages/excalidraw/index";
import {
  save,
  PlusIcon,
  file,
  ExportIcon,
  LoadIcon,
  share,
} from "../../packages/excalidraw/components/icons";
import {
  useExcalidrawActionManager,
  useExcalidrawSetAppState,
  useExcalidrawElements,
} from "../../packages/excalidraw/components/App";
import { actionLoadScene } from "../../packages/excalidraw/actions";
import { openConfirmModal } from "../../packages/excalidraw/components/OverwriteConfirm/OverwriteConfirmState";
import { useI18n } from "../../packages/excalidraw/i18n";
import Trans from "../../packages/excalidraw/components/Trans";
import { LanguageList } from "./LanguageList";
import { useOpenAgentsDrawer } from "./agents/AgentsSidebar";
import { plugIcon } from "./agents/plugIcon";

/**
 * "Export" — the JSON-scene export. Same action as
 * `MainMenu.DefaultItems.Export`, but relabelled "Export" (not the shared
 * "Save to..." locale string, which reads as a cloud save one row below the
 * actual cloud-save item). Re-implemented app-side so we don't touch core or
 * the shared `en.json` key.
 */
const ExportItem: React.FC = () => {
  const setAppState = useExcalidrawSetAppState();
  return (
    <MainMenu.Item
      icon={ExportIcon}
      data-testid="app-json-export-button"
      onSelect={() => setAppState({ openDialog: { name: "jsonExport" } })}
    >
      Export
    </MainMenu.Item>
  );
};

/**
 * "Import from file" — loads a scene from a local `.excalidraw` file. Same
 * action (and same non-empty-canvas overwrite guard) as
 * `MainMenu.DefaultItems.LoadScene`, relabelled from the shared "Open" locale
 * string. Keeps the confirm modal so importing never silently discards work.
 */
const ImportFromFileItem: React.FC = () => {
  const { t } = useI18n();
  const actionManager = useExcalidrawActionManager();
  const elements = useExcalidrawElements();

  if (!actionManager.isActionEnabled(actionLoadScene)) {
    return null;
  }

  const handleSelect = async () => {
    if (
      !elements.length ||
      (await openConfirmModal({
        title: t("overwriteConfirm.modal.loadFromFile.title"),
        actionLabel: t("overwriteConfirm.modal.loadFromFile.button"),
        color: "warning",
        description: (
          <Trans
            i18nKey="overwriteConfirm.modal.loadFromFile.description"
            bold={(text) => <strong>{text}</strong>}
            br={() => <br />}
          />
        ),
      }))
    ) {
      actionManager.executeAction(actionLoadScene);
    }
  };

  return (
    <MainMenu.Item
      icon={LoadIcon}
      data-testid="app-load-button"
      onSelect={handleSelect}
    >
      Import from file
    </MainMenu.Item>
  );
};

/**
 * "Share…" — the menu entry into the share flow. On mobile the top-right
 * Share button doesn't render (no top-right UI), so the ☰ menu is the only
 * way in; App opens the SharePopover as a centered modal from here. Exported
 * so it can be exercised in isolation (it only depends on the `onSelect`
 * prop, not the Excalidraw context).
 */
export const ShareMenuItem: React.FC<{ onSelect: () => void }> = ({
  onSelect,
}) => (
  <MainMenu.Item icon={share} data-testid="menu-share-item" onSelect={onSelect}>
    Share…
  </MainMenu.Item>
);

/**
 * "Agents" — opens the Agents drawer in place (the `/agents` page stays as
 * the deep link agents and the consent flow use).
 */
export const AgentsMenuItem: React.FC = () => {
  const openAgents = useOpenAgentsDrawer();
  return (
    <MainMenu.Item
      icon={plugIcon}
      data-testid="menu-agents"
      onSelect={openAgents}
    >
      Agents
    </MainMenu.Item>
  );
};

export const AppMainMenu: React.FC<{
  onCollabDialogOpen: () => any;
  isCollaborating: boolean;
  isCollabEnabled: boolean;
  showSaveToCloud?: boolean;
  onSaveToCloud?: () => void;
  showNewMindmap?: boolean;
  onNewMindmap?: () => void;
  showShare?: boolean;
  onShareOpen?: () => void;
}> = React.memo((props) => {
  return (
    <MainMenu>
      {props.showNewMindmap && (
        <MainMenu.Item icon={PlusIcon} onSelect={() => props.onNewMindmap?.()}>
          New mindmap
        </MainMenu.Item>
      )}
      {props.showSaveToCloud && (
        <MainMenu.Item icon={save} onSelect={() => props.onSaveToCloud?.()}>
          {/* "Save to cloud", NOT "Save to My Files": it sits one row above
              Excalidraw's built-in "Save to..." (export to a local file) and
              users could not tell the two apart. The primary affordance for
              this is now the Save button next to Share (top-right). */}
          Save to cloud
        </MainMenu.Item>
      )}
      <MainMenu.Item
        icon={file}
        onSelect={() => {
          window.location.href = "/files";
        }}
      >
        My Files…
      </MainMenu.Item>
      {props.showShare && (
        <ShareMenuItem onSelect={() => props.onShareOpen?.()} />
      )}
      <ImportFromFileItem />
      <MainMenu.DefaultItems.SaveToActiveFile />
      <ExportItem />
      {/* <MainMenu.DefaultItems.SaveAsImage /> */}
      {props.isCollabEnabled && (
        <MainMenu.DefaultItems.LiveCollaborationTrigger
          isCollaborating={props.isCollaborating}
          onSelect={() => props.onCollabDialogOpen()}
        />
      )}

      <MainMenu.DefaultItems.Help />
      <MainMenu.DefaultItems.ClearCanvas />
      <AgentsMenuItem />
      <MainMenu.Separator />
      {/* <MainMenu.ItemLink
        icon={PlusPromoIcon}
        href={`${
          import.meta.env.VITE_APP_PLUS_LP
        }/plus?utm_source=excalidraw&utm_medium=app&utm_content=hamburger`}
        className="ExcalidrawPlus"
      >
        Excalidraw+
      </MainMenu.ItemLink> */}
      <MainMenu.DefaultItems.Socials />
      <MainMenu.Separator />
      <MainMenu.DefaultItems.ToggleTheme />
      <MainMenu.ItemCustom>
        <LanguageList style={{ width: "100%" }} />
      </MainMenu.ItemCustom>
      <MainMenu.DefaultItems.ChangeCanvasBackground />
    </MainMenu>
  );
});
